// Platform & custom-map offerings for Anne Flinn Advertising.

export const wineryTiers = [
  {
    key: "free",
    name: "Standard Listing",
    price: "Free",
    cadence: "always",
    tagline: "The foundation, every winery in Virginia is welcome.",
    bullets: [
      "Searchable directory listing",
      "Map pin + region tag",
      "Address, hours, and one-click directions",
      "Visible on web + mobile app",
    ],
    cta: "Already in the directory",
    highlight: false,
  },
  {
    key: "featured",
    name: "Featured Winery",
    price: "$79",
    cadence: "/ month",
    tagline: "Stand out the moment a visitor opens the map.",
    bullets: [
      "Everything in Standard, plus:",
      "Hero photo on your detail page",
      "Embedded events & tasting calendar",
      "Rotation in the 'Featured Wineries' carousel",
      "Top-of-region map pin priority",
      "Monthly performance report (clicks, directions, visits)",
    ],
    cta: "Upgrade to Featured →",
    highlight: true,
  },
  {
    key: "premium",
    name: "Premium Partner",
    price: "$199",
    cadence: "/ month",
    tagline: "Own your region. Push to nearby visitors. Drive the click.",
    bullets: [
      "Everything in Featured, plus:",
      "Permanent top-of-region map pin",
      "Push notifications to app users near you",
      "Monthly email-blast inclusion",
      "Retargeting pixel on your listing",
      "Priority support + custom landing-page integration",
    ],
    cta: "Become a Premium Partner →",
    highlight: false,
  },
];

export const customMapVerticals = [
  { name: "Breweries & Distilleries", icon: "Beer" },
  { name: "Cideries & Meaderies", icon: "Wine" },
  { name: "B&Bs, Inns & Boutique Hotels", icon: "BedDouble" },
  { name: "Glamping & Outdoor Stays", icon: "Tent" },
  { name: "Farm-to-Table Restaurants", icon: "Utensils" },
  { name: "Historic Sites & Museums", icon: "Landmark" },
  { name: "Fall Foliage Routes", icon: "Leaf" },
  { name: "Holiday Lights Tours", icon: "Sparkles" },
  { name: "Wedding & Event Venues", icon: "Heart" },
  { name: "Art Galleries & Studios", icon: "Palette" },
  { name: "Agritourism Farms", icon: "Wheat" },
  { name: "Anything Else You Can Map", icon: "MapPin" },
];

// Who benefits from the platform — used on /platforms
// Each persona speaks directly to a buyer's pain + outcome.
export const platformPersonas = [
  {
    key: "wineries",
    eyebrow: "For Wineries · Breweries · Listable Tourism Businesses",
    icon: "Wine",
    headline: "If you're not on the map, you don't exist.",
    body: "When someone opens the app to plan a trip, every map pin is a discovery moment. A free listing gets you found. Featured upgrades put your hero photo, events, and tasting calendar in the foreground. Premium pins your spot at the top of the region — and push-notifies visitors who are already nearby.",
    cta: { label: "See listing tiers", anchor: "#winery-tiers" },
    accent: "lime",
  },
  {
    key: "dmos",
    eyebrow: "For DMOs · CVBs · Regional Tourism Boards",
    icon: "Landmark",
    headline: "Solve your thin-content SEO problem.",
    body: "Most DMO sites struggle in Google because they're built on the same boilerplate brochure copy on every page. Search engines see thin content, not a destination. We give you 100+ genuinely useful, searchable detail pages — one per attraction, with real data: hours, region, photos, events, directions. That's the kind of content Google actually rewards. Your visitors get a tool they can use. You get the SEO surface and the rankings you've been missing.",
    cta: { label: "Talk to us about your region", anchor: "#lead-form" },
    accent: "lime",
  },
  {
    key: "hosts",
    eyebrow: "For Hotels · Airbnb Hosts · STR Operators",
    icon: "BedDouble",
    headline: "Your concierge, in your guest's pocket.",
    body: "Hand your guests a curated app the moment they check in. Wineries within 20 minutes. Restaurants you vouch for. Breweries on the route home. Push notifications for tonight's live music. Guests leave saying 'they helped me discover everything' — and that's a 5-star review you didn't have to chase.",
    cta: { label: "Get your guest-experience platform", anchor: "#lead-form" },
    accent: "lime",
  },
  {
    key: "brands",
    eyebrow: "For Trade Associations · Brands · Any Vertical",
    icon: "Layers",
    headline: "Customizable for your industry — ready to launch immediately.",
    body: "Same platform engine, your brand. Directory + interactive map + mobile app, customized for your category. Fall foliage routes, agritourism farms, wedding venues, breweries — whatever your niche is, the engine is built. We customize, you launch in 6–10 weeks: discovery, build, data ingestion, and a paid-media launch campaign across streaming, social, and search.",
    cta: { label: "Discuss a build", anchor: "#lead-form" },
    accent: "lime",
  },
];

// What ships with every custom build — used on /platforms
export const platformDeliverables = [
  { icon: "MapPin", t: "Interactive Map", d: "Leaflet-powered, region-tagged, mobile-first." },
  { icon: "Smartphone", t: "Native Mobile App", d: "iOS + responsive web app, push-ready." },
  { icon: "Globe", t: "SEO Detail Pages", d: "One per listing — the content Google actually ranks." },
  { icon: "Database", t: "Admin Tooling", d: "Add listings, post events, run reports. No dev needed." },
  { icon: "Bell", t: "Push Notifications", d: "Your direct line to engaged, opted-in users." },
  { icon: "TrendingUp", t: "Analytics Dashboard", d: "Visits, clicks, directions, region performance — monthly." },
  { icon: "Layers", t: "Customizable Per Industry", d: "Wineries, foliage, hotels, agritourism — your category, your brand." },
  { icon: "Sparkles", t: "Ready to Launch — Fast", d: "Engine already built. Your platform ships in 6–10 weeks." },
];

export const buildSteps = [
  {
    n: "01",
    t: "Discovery",
    d: "We map your niche, your geography, and your audience. We define your data model, branding, and success metrics, in one working session.",
  },
  {
    n: "02",
    t: "Build",
    d: "We design and ship your branded directory + interactive map + mobile app, in 6–10 weeks. You get launch creative, a verified data set, and an admin tool to manage listings.",
  },
  {
    n: "03",
    t: "Launch & Grow",
    d: "We run the launch campaign across streaming + social + paid search. Then we operate the platform with you, ad sales, listing upgrades, monthly performance reports.",
  },
];
